import { useState } from "react";
import { createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  createFieldAdminService,
  deleteFieldAdminService,
  editFieldAdminService,
  getFieldsAdminService,
} from "./../../../services/adminServices";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const fieldAdminContext = createContext({
  fields: [],
  setFields: () => {},
  field: {},
  setField: () => {},
  filter: "",
  setFilter: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  type: "",
  setType: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetFields: () => {},
  handleCreateField: () => {},
  handleEditField: () => {},
  handleDeleteField: () => {},
});

const FieldAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [fields, setFields] = useState([]);
  const [field, setField] = useState({});
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("");
  const [type, setType] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setField(val);
      setValue("name", val.name);
      setValue("label", val.label);
      setValue("type", val.type);
      setValue(
        "options",
        val.options.map((_o) => ({ value: _o, label: _o }))
      );
      setValue("isRequired", val.isRequired);
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setField({});
  };

  // Get Fields
  const handleGetFields = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      ...(type && { type }),
      filter,
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getFieldsAdminService(objData);
      if (status === 200) {
        setFields(data.result?.fields);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Field
  const handleCreateField = async (fieldData) => {
    const objData = {
      ...fieldData,
      options: fieldData.options?.map((_o) => _o.value),
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createFieldAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetFields();
        handleHideModal();
      }
    } catch ({ response }) {
      
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Field
  const handleEditField = async (fieldData) => {
    const objData = {
      id: field._id,
      ...fieldData,
      options: fieldData.options?.map((_o) => _o.value),
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editFieldAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetFields();
        handleHideModal();
      }
    } catch ({ response }) {
      
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Field
  const handleDeleteField = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteFieldAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetFields();
      }
    } catch ({ response }) {
      
      if (response && response.status)
        toastErrorMessage(response.data.resultMessage);
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <fieldAdminContext.Provider
      value={{
        fields,
        setFields,
        field,
        setField,
        filter,
        setFilter,
        sort,
        setSort,
        type,
        setType,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetFields,
        handleCreateField,
        handleEditField,
        handleDeleteField,
      }}
    >
      {children}
    </fieldAdminContext.Provider>
  );
};

export default FieldAdminContext;
